<script setup>
import { toRef } from 'vue';
import { useEvolutionReconnect } from 'dashboard/composables/useEvolutionReconnect';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import EvolutionPublicLinkShare from 'dashboard/components/EvolutionPublicLinkShare.vue';
import QrCodeAutoRefreshIndicator from 'dashboard/components/QrCodeAutoRefreshIndicator.vue';
import EvolutionNumberMismatchDialog from 'dashboard/components/EvolutionNumberMismatchDialog.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

// Reconnecting is scanning a QR with the phone that owns the session, so anyone with access to
// the inbox can do it - the Evolution credentials stay server side (see
// InboxesController#evolution_qrcode), they're never handed to the browser.
const {
  showQrPanel,
  isLoadingQrcode,
  isPolling,
  qrcodeBase64,
  publicLinkUrl,
  secondsRemaining,
  needsManualRefresh,
  totalSeconds,
  requestAuthorization,
  refreshQrcode,
  activeMismatch,
  pendingMismatch,
  isResolvingMismatch,
  reviewMismatch,
  closeMismatch,
  acceptNumberChange,
  declineNumberChange,
} = useEvolutionReconnect(toRef(props, 'inbox'));
</script>

<template>
  <div class="px-3 pt-3">
    <Banner
      v-if="pendingMismatch && !showQrPanel"
      color="amber"
      :action-label="
        $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.NUMBER_MISMATCH.REVIEW')
      "
      @action="reviewMismatch"
    >
      {{
        $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.NUMBER_MISMATCH.BANNER', {
          scanned: `+${pendingMismatch.phone_number}`,
        })
      }}
    </Banner>
    <Banner
      v-else-if="!showQrPanel"
      color="ruby"
      :action-label="
        $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.RECONNECT.ACTION_LABEL')
      "
      @action="requestAuthorization"
    >
      {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.RECONNECT.DESC') }}
    </Banner>
    <div
      v-else
      class="flex flex-col items-center gap-3 py-3 rounded-xl border border-n-ruby-4 bg-n-ruby-3"
    >
      <img
        v-if="qrcodeBase64"
        :src="qrcodeBase64"
        :alt="$t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.QR.TITLE')"
        class="size-48 rounded-lg border border-n-weak bg-white"
      />
      <p v-if="isPolling" class="text-xs text-n-slate-11 px-2 text-center">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.QR.WAITING') }}
      </p>
      <EvolutionPublicLinkShare v-if="publicLinkUrl" :url="publicLinkUrl" />
      <QrCodeAutoRefreshIndicator
        :seconds-remaining="secondsRemaining"
        :total-seconds="totalSeconds"
        :needs-manual-refresh="needsManualRefresh"
        :is-loading="isLoadingQrcode"
        @refresh="refreshQrcode"
      />
    </div>
    <EvolutionNumberMismatchDialog
      :inbox="inbox"
      :mismatch="activeMismatch"
      :is-loading="isResolvingMismatch"
      @accept="acceptNumberChange"
      @decline="declineNumberChange"
      @close="closeMismatch"
    />
  </div>
</template>
